/* ==========================================================================
   SORTS SHOPIFY CANNOT DO

   Shopify's storefront sorts a collection by title, price, date, best-selling
   or manual position -- and nothing else. Discount is not on that list, so
   "Discount $" and "Discount %" are computed here instead.

   "Best Selling" is on the list, but only nominally: it ranks by all-time
   orders and falls back to newest-first for anything with none, so on a store
   without sales it is just Recent under another name. While the seed setting
   is on, this file ranks it by market sales data instead. Switch the seed off
   once real orders exist and Shopify takes it back.

   When one of these is selected, this script takes the grid over: it reads a
   prebuilt index of the catalogue (assets/synergy-sort-index.json, written by
   tools/shopify-import/build-sort-index.mjs), applies the same collection and
   sidebar filters the URL describes, sorts, and renders the cards
   itself, with its own pagination. Every other sort is Shopify's own and this
   file does nothing at all.

   The index is fetched lazily -- only when one of these sorts is actually
   chosen -- so normal browsing never pays for it.
   ========================================================================== */

(function () {
  'use strict';

  const COMPARE = {
    'discount-amount': (a, b) => b.discount - a.discount,
    'discount-percent': (a, b) =>
      (b.compare ? b.discount / b.compare : 0) - (a.compare ? a.discount / a.compare : 0),

    /* Best Selling, seeded. Shopify ranks by all-time orders and quietly falls
       back to newest-first for anything with none -- which, before the store
       has sales, is the whole catalogue, so the sort becomes a duplicate of
       Recent. Until real orders exist we rank by market sales instead: units a
       month first, then sales rank as the tie-break. Turning the seed off in
       the section settings hands the sort straight back to Shopify.

       The index publishes only a POSITION, not the underlying figures. It is
       served from the CDN to anyone who asks for it, and units a month and
       sales rank per product is competitive data we buy -- the sort needs the
       order, not the numbers. */
    'best-selling': (a, b) => (a.rank || b.rank)
      /* old index: units a month, then sales rank */
      ? (b.sold - a.sold) || ((a.rank || Infinity) - (b.rank || Infinity))
      /* new index: an opaque position, lowest first, 0 meaning no data */
      : ((a.pos || Infinity) - (b.pos || Infinity)),
  };

  // Discount is never Shopify's to do. Best Selling is, once there is
  // something to rank -- so it is ours only while the seed is switched on.
  const isOurs = (sort) =>
    sort === 'discount-amount' || sort === 'discount-percent' ||
    (sort === 'best-selling' && window.SynergyVelocitySeed === true);

  const GENERIC_TYPES = ['fragrance', 'bundle', 'gift set', 'accessories', 'other'];

  const grid = () => document.querySelector('.syn-grid');
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const money = (cents) => {
    const fmt = window.themeVariables?.settings?.moneyFormat;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && fmt) {
      return window.Shopify.formatMoney(cents, fmt);
    }
    return '$' + (cents / 100).toFixed(2);
  };

  const escapeHtml = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* ---------- The index ---------- */

  let indexPromise = null;
  function loadIndex() {
    if (indexPromise) return indexPromise;
    const url = window.SynergySortIndexUrl;
    if (!url) return Promise.reject(new Error('no index url'));
    indexPromise = fetch(url)
      .then((r) => r.json())
      .then((raw) => ({
        collections: raw.collections,
        imagePrefix: raw.imagePrefix || '',
        products: raw.products.map((r) => ({
          handle: r[0], title: r[1], vendor: r[2], type: r[3], image: r[4],
          price: r[5], compare: r[6], size: r[7], available: !!r[8],
          collections: r[9], variants: r[10], variantId: r[11],
          pos: r[12] || 0, sold: r[12] || 0, rank: r[13] || 0,
          discount: r[6] && r[6] > r[5] ? r[6] - r[5] : 0,
        })),
      }));
    return indexPromise;
  }

  /* ---------- What the URL is asking for ---------- */

  function readRequest() {
    const params = new URLSearchParams(window.location.search);
    const sort = params.get('sort_by') || '';
    if (!isOurs(sort)) return null;

    const match = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    return {
      sort,
      collection: match ? match[1] : null,
      page: Math.max(1, Number(params.get('page') || 1)),
      vendors: params.getAll('filter.p.vendor'),
      types: params.getAll('filter.p.product_type'),
      tags: params.getAll('filter.p.tag'),
      priceMin: params.get('filter.v.price.gte'),
      priceMax: params.get('filter.v.price.lte'),
      inStock: params.getAll('filter.v.availability').includes('1'),
      params,
    };
  }

  function selectProducts(index, req) {
    let list = index.products;

    // The collection itself. /collections/all carries no membership rule.
    if (req.collection && req.collection !== 'all') {
      const ci = index.collections.indexOf(req.collection);
      if (ci === -1) return null;            // a collection we have no index for
      list = list.filter((p) => p.collections.includes(ci));
    }

    // The sidebar's own filters, applied exactly as Shopify would.
    if (req.vendors.length) list = list.filter((p) => req.vendors.includes(p.vendor));
    if (req.types.length) list = list.filter((p) => req.types.includes(p.type));
    if (req.tags.length) {
      const ids = req.tags.map((t) => index.collections.indexOf(t.toLowerCase().replace(/\s+/g, '-')));
      list = list.filter((p) => ids.some((i) => i !== -1 && p.collections.includes(i)));
    }
    if (req.priceMin) list = list.filter((p) => p.price >= Number(req.priceMin) * 100);
    if (req.priceMax) list = list.filter((p) => p.price <= Number(req.priceMax) * 100);
    if (req.inStock) list = list.filter((p) => p.available);

    const compare = COMPARE[req.sort];
    // Sold-out products fall to the back, as they do in every other sort here.
    return list.slice().sort((a, b) => {
      if (a.available !== b.available) return a.available ? -1 : 1;
      const d = compare(a, b);
      return d !== 0 ? d : a.title.localeCompare(b.title);
    });
  }

  /* ---------- Cards ---------- */

  function cardHtml(p, index, opts) {
    const url = root() + 'products/' + p.handle;
    const src = index.imagePrefix + p.image;
    const img = p.image ? src + (src.includes('?') ? '&' : '?') + 'width=900' : '';

    const multi = p.variants && p.variants.length > 1;
    // A product sold in several sizes shows none: naming one would be wrong.
    const size = multi ? '' : p.size;
    const concentration = GENERIC_TYPES.includes((p.type || '').toLowerCase()) ? '' : p.type;

    let badge = '';
    if (!p.available) {
      badge = '<span class="syn-badge syn-badge--soldout">Sold out</span>';
    } else if (p.discount) {
      const text = opts.discountMode === 'percentage'
        ? 'Save ' + Math.round((p.discount / p.compare) * 100) + '%'
        : 'Save ' + money(p.discount);
      badge = '<span class="syn-badge">' + text + '</span>';
    }

    const plus = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';
    const actions = opts.showActions
      ? '<div class="syn-card__actions">' +
          '<synergy-wish-button handle="' + escapeHtml(p.handle) + '">' +
            '<button type="button" class="syn-icon-btn" aria-pressed="false" aria-label="Add ' + escapeHtml(p.title) + ' to wishlist">' +
              '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path class="syn-heart-path" d="M12 20.5C7 16.5 3 12.9 3 9a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 3.9-4 7.5-9 11.5Z"/></svg>' +
            '</button>' +
          '</synergy-wish-button>' +
          (p.available
            ? (multi
                // Several sizes to choose from: the choice belongs on the product page.
                ? '<a class="syn-icon-btn" href="' + url + '" aria-label="Choose a size for ' + escapeHtml(p.title) + '">' + plus + '</a>'
                : '<button type="button" class="syn-icon-btn" data-syn-quick-add="' + p.variantId + '" aria-label="Add ' + escapeHtml(p.title) + ' to bag">' + plus + '</button>')
            : '') +
        '</div>'
      : '';

    const prices = p.discount
      ? '<span class="syn-card__price syn-card__price--sale">' + money(p.price) + '</span>' +
        '<span class="syn-card__price syn-card__price--compare">' + money(p.compare) + '</span>'
      : '<span class="syn-card__price">' + money(p.price) + '</span>';

    return (
      '<product-card class="product-card syn-card" handle="' + escapeHtml(p.handle) + '">' +
        '<div class="syn-card__figure">' +
          '<div class="syn-card__badges">' + badge + '</div>' +
          actions +
          '<a href="' + url + '" class="syn-card__media" draggable="false" data-instant aria-label="' + escapeHtml(p.title) + '">' +
            (img ? '<img src="' + escapeHtml(img) + '" alt="' + escapeHtml(p.title) + '" width="900" height="900" loading="lazy" draggable="false" class="syn-card__image--primary">' : '') +
          '</a>' +
        '</div>' +
        '<div class="syn-card__info">' +
          (p.vendor ? '<span class="syn-card__brand">' + escapeHtml(p.vendor) + '</span>' : '') +
          '<a href="' + url + '" class="syn-card__title" data-instant>' + escapeHtml(p.title) + '</a>' +
          (size ? '<span class="syn-card__size">' + escapeHtml(size) + '</span>' : '') +
          (concentration ? '<span class="syn-card__concentration">' + escapeHtml(concentration) + '</span>' : '') +
          '<div class="syn-card__prices">' + prices + '</div>' +
        '</div>' +
      '</product-card>'
    );
  }

  /* ---------- Pagination ---------- */

  function pagerHtml(page, pages, params) {
    if (pages < 2) return '';
    const href = (n) => {
      const p = new URLSearchParams(params);
      if (n === 1) p.delete('page'); else p.set('page', String(n));
      const q = p.toString();
      return window.location.pathname + (q ? '?' + q : '');
    };

    // A window around the current page, so 50 pages don't print 50 links.
    const nums = [];
    for (let n = 1; n <= pages; n++) {
      if (n === 1 || n === pages || Math.abs(n - page) <= 2) nums.push(n);
      else if (nums[nums.length - 1] !== '…') nums.push('…');
    }

    return (
      '<nav class="syn-sort-pagination" aria-label="Pagination">' +
        (page > 1 ? '<a href="' + href(page - 1) + '" rel="prev">Previous</a>' : '') +
        nums.map((n) =>
          n === '…'
            ? '<span class="syn-sort-pagination__gap">…</span>'
            : (n === page
                ? '<span aria-current="page">' + n + '</span>'
                : '<a href="' + href(n) + '">' + n + '</a>')
        ).join('') +
        (page < pages ? '<a href="' + href(page + 1) + '" rel="next">Next</a>' : '') +
      '</nav>'
    );
  }

  /* ---------- Render ---------- */

  async function run() {
    const req = readRequest();
    if (!req) return;

    const el = grid();
    if (!el) return;

    // Keep the sidebar radio on the option the shopper actually chose --
    // Shopify does not recognise these values, so it cannot check them itself.
    document.querySelectorAll('input[name="sort_by"]').forEach((input) => {
      input.checked = input.value === req.sort;
    });

    let index;
    try {
      index = await loadIndex();
    } catch (e) {
      return;                                // leave Shopify's own grid in place
    }

    const list = selectProducts(index, req);
    if (!list) return;

    const perPage = Number(el.getAttribute('data-syn-per-page')) || 24;
    const opts = {
      showActions: el.getAttribute('data-syn-show-actions') !== 'false',
      discountMode: el.getAttribute('data-syn-discount-mode') || 'percentage',
    };

    const pages = Math.max(1, Math.ceil(list.length / perPage));
    const page = Math.min(req.page, pages);
    const slice = list.slice((page - 1) * perPage, page * perPage);

    el.setAttribute('data-syn-sorted', req.sort);
    el.innerHTML = slice.map((p) => cardHtml(p, index, opts)).join('');

    // Shopify's pagination counts its own ordering, so replace it with ours.
    const oldPager = el.parentElement && el.parentElement.querySelector('.pagination');
    if (oldPager) oldPager.remove();
    const existing = document.querySelector('.syn-sort-pagination');
    if (existing) existing.remove();
    el.insertAdjacentHTML('afterend', pagerHtml(page, pages, req.params));

    const count = document.querySelector('.syn-toolbar__count');
    if (count) count.textContent = list.length === 1 ? '1 product' : list.length + ' products';
  }

  /* ---------- Quick add ---------- */

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-syn-quick-add]');
    if (!button) return;
    event.preventDefault();
    button.disabled = true;
    try {
      const res = await fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: Number(button.getAttribute('data-syn-quick-add')), quantity: 1 }] }),
      });
      if (res.ok) {
        const cart = await (await fetch(root() + 'cart.js')).json();
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true, detail: { baseEvent: 'variant:add', cart: cart },
        }));
      }
    } catch (e) { /* leave the card as it was */ }
    button.disabled = false;
  });

  /* Applying a filter or a sort does not reload the page: the theme fetches the
     section's HTML and swaps it in, which puts Shopify's own ordering back into
     a grid we had already reordered. Nothing is dispatched after that swap, so
     watch for it and re-render.

     The re-render is itself a mutation, hence the marker check -- a grid
     already showing the sort in the URL is left alone. */
  let pending = null;
  new MutationObserver(() => {
    window.clearTimeout(pending);
    pending = window.setTimeout(() => {
      const req = readRequest();
      const el = grid();
      if (!req || !el) return;
      if (el.getAttribute('data-syn-sorted') === req.sort) return;
      run();
    }, 30);
  }).observe(document.body, { childList: true, subtree: true });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
